import type React from 'react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';
import FormField from '@/components/atoms/FormField';
import TextField from '@/components/atoms/TextField';
import { ModalBody, ModalComponent, ModalFooter } from '@/components/molecules/ModalComponent';

import styles from './ModalCreateBioscopeContract.module.scss';

export interface NewContractFormValues {
    description: string;
    count: number;
}

export interface ModalCreateBioscopeContractProps {
    onSubmitted: (ev: NewContractFormValues) => void;
}

/** A whole, non-negative bioscope count, or undefined while the field holds anything else. */
function parseCount(text: string): number | undefined {
    if (text.trim() === '') return undefined;
    const count = Number(text);
    return Number.isInteger(count) && count >= 0 ? count : undefined;
}

function ModalCreateBioscopeContract({ onSubmitted }: ModalCreateBioscopeContractProps) {
    const { t } = useTranslation();
    const openButtonId = useId();
    const descriptionId = useId();
    const contractNumberId = useId();
    const [open, setOpen] = useState(false);
    // Both fields hold what was typed, so the inputs stay controlled from the first render
    // and an emptied count reads as empty rather than NaN.
    const [description, setDescription] = useState('');
    const [countText, setCountText] = useState('');

    const count = parseCount(countText);
    const isValid = count !== undefined && description !== '';

    const toggle = () => setOpen(!open);

    const onFormDataonSubmit = (ev: React.FormEvent<HTMLFormElement>) => {
        ev.preventDefault();
        if (count !== undefined && description !== '') {
            onSubmitted({ description, count });
            setOpen(false);
        }
    };

    const onCancel = () => {
        setDescription('');
        setCountText('');
        setOpen(false);
    };
    return (
        <div className={styles.ModalCreateBioscopeContract}>
            <Button
                id={openButtonId}
                data-testid="openModalCreateBioscopeContract"
                onClick={() => setOpen(true)}>
                {t('bioscopeContracts.createModal.open')}
            </Button>
            <ModalComponent
                isOpen={open}
                onClose={toggle}
                centered={false}
                title={t('bioscopeContracts.createModal.title')}>
                <form onSubmit={onFormDataonSubmit} onReset={onCancel}>
                    <ModalBody>
                        <FormField
                            label={t('molecules.common.description')}
                            htmlFor={descriptionId}>
                            <TextField
                                id={descriptionId}
                                name="ContractDescription"
                                type="text"
                                value={description}
                                onChange={(ev) => setDescription(ev.target.value)}
                                placeholder={t('')}
                                required={false}
                            />
                        </FormField>
                        <FormField
                            label={t('bioscopeContracts.createModal.bioscopeCount')}
                            htmlFor={contractNumberId}>
                            <TextField
                                id={contractNumberId}
                                name="ContractNumber"
                                type="number"
                                min={0}
                                step={1}
                                value={countText}
                                onChange={(ev) => setCountText(ev.target.value)}
                                placeholder={t('')}
                                required={false}
                            />
                        </FormField>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="secondary" type="reset">
                            {t('common.actions.cancel')}
                        </Button>
                        <Button variant="primary" type="submit" disabled={!isValid}>
                            {t('bioscopeContracts.createModal.submit')}
                        </Button>
                    </ModalFooter>
                </form>
            </ModalComponent>
        </div>
    );
}

export default ModalCreateBioscopeContract;
export { ModalCreateBioscopeContract };
